/**
 * What time it is where the business is.
 *
 * Every timestamp is stored as UTC, which is right: an instant is an instant,
 * and a clock-in that moved because a server was redeployed in another region
 * would be a worse bug than any this module fixes. What was missing is the
 * other half — the conversion back. A stored `2026-10-13T13:33:00.000Z` was
 * being read by slicing `HH:MM` straight out of the string, which answers
 * "what time was it in Greenwich", a question nobody asked.
 *
 * It is not only a display fault. An employee's working day is written
 * `09:00`–`17:00`, and the half-day arithmetic in `clock.ts` was laying that
 * window over UTC midnight. At UTC−4 that puts a nine-to-five entirely outside
 * the window it is supposed to be measured against: the morning reads as
 * uncovered and the day comes back half worked, which is a deduction somebody
 * discovers on a payslip.
 *
 * So the rule is one direction each way, both through here:
 *
 *   an instant → a wall clock    `businessDate`, `timeOfDay`, `minutesIntoDay`
 *   a wall clock → an instant    `instantAt`, `dayStartMs`
 *
 * Nothing else should read `getUTCHours` or slice characters 11–16 off a
 * timestamp. A calendar *date* on its own — `datesBetween`, a weekday from a
 * `YYYY-MM-DD` — is not an instant and does not come through here; parsing
 * those at UTC midnight is correct and stays as it is.
 */

/**
 * The zone the business keeps its hours in.
 *
 * The British Virgin Islands: Atlantic Standard Time, UTC−4, and — unlike most
 * of the Americas — no daylight saving, so the offset is the same in January as
 * in July. The conversions below go through `Intl` rather than subtracting four
 * hours anyway, so moving this line to a zone that *does* shift would not
 * quietly produce wrong answers twice a year.
 *
 * One constant rather than a column: this is the employer's own locale, not a
 * policy figure like a rate or a threshold, and an employer does not have two.
 * If it ever has to be editable it belongs on the payroll rates record, which
 * is already the home for everything a deploy should not own.
 */
export const BUSINESS_TIME_ZONE = "America/Tortola";

/** `HH:MM` to minutes past midnight. Kept local so this module imports nothing. */
function parseTimeOfDay(time: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) throw new Error(`Not a time of day: ${time}`);

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) throw new Error(`Not a time of day: ${time}`);
  return hours * 60 + minutes;
}

/*
 * `h23` rather than `hour12: false`, which renders midnight as `24` under some
 * ICU versions and would put every clock-in after 23:59.
 */
const WALL_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: BUSINESS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function asInstant(at: string | Date): Date {
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) throw new Error(`Not a timestamp: ${String(at)}`);
  return date;
}

/** What the clock in the business zone reads at this instant. */
function wallClock(at: Date): WallClock {
  const got: Record<string, string> = {};
  for (const part of WALL_CLOCK.formatToParts(at)) {
    if (part.type !== "literal") got[part.type] = part.value;
  }

  return {
    year: Number(got.year),
    month: Number(got.month),
    day: Number(got.day),
    hour: Number(got.hour),
    minute: Number(got.minute),
    second: Number(got.second),
  };
}

/**
 * The business zone's offset from UTC at a given instant, in minutes.
 *
 * `−240` for the British Virgin Islands, always. Worked out by reading the wall
 * clock back as though it were UTC and taking the difference, which is the one
 * way to get this that does not need a table of its own.
 */
export function offsetMinutes(at: string | Date = new Date()): number {
  const instant = asInstant(at);
  const wall = wallClock(instant);
  const asIfUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );

  // Floored to the second, because the formatted parts have no milliseconds and
  // the difference would otherwise carry the remainder.
  return (asIfUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000;
}

/** The business-zone calendar date an instant falls on — `2026-10-13`. */
export function businessDate(at: string | Date = new Date()): string {
  const wall = wallClock(asInstant(at));
  const month = String(wall.month).padStart(2, "0");
  const day = String(wall.day).padStart(2, "0");
  return `${wall.year}-${month}-${day}`;
}

/**
 * The business-zone time of day an instant falls at — `09:33`, 24-hour.
 *
 * Replaces `timestamp.slice(11, 16)` everywhere it appeared. Same shape, same
 * four characters and a colon, but the right four.
 */
export function timeOfDay(at: string | Date): string {
  const wall = wallClock(asInstant(at));
  return `${String(wall.hour).padStart(2, "0")}:${String(wall.minute).padStart(2, "0")}`;
}

/** Minutes past business-zone midnight — what the working-day window compares against. */
export function minutesIntoDay(at: string | Date): number {
  const wall = wallClock(asInstant(at));
  return wall.hour * 60 + wall.minute;
}

/**
 * The instant at which a given wall-clock time occurs on a given date — the
 * inverse of `timeOfDay`, for a time somebody typed.
 *
 * Resolved twice: the first guess uses the offset in force at the naive
 * instant, which is itself up to an offset away from the answer, so it is
 * corrected again with the offset in force there. That settles it for any zone,
 * including one whose offset shifts between the two.
 */
export function instantAt(date: string, time: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!match) throw new Error(`Not a date: ${date}`);

  const naive = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    parseTimeOfDay(time),
  );

  const firstGuess = naive - offsetMinutes(new Date(naive)) * 60_000;
  return new Date(naive - offsetMinutes(new Date(firstGuess)) * 60_000).toISOString();
}

/**
 * Epoch milliseconds at business-zone midnight opening a date.
 *
 * The base the working-day window is measured from. Adding minutes to it
 * assumes the offset does not shift partway through a working day, which holds
 * for a zone without daylight saving and is in any case a day a payroll would
 * want looked at by a person.
 */
export function dayStartMs(date: string): number {
  return Date.parse(instantAt(date, "00:00"));
}

/** Today's date in the business zone. */
export function businessToday(now: Date = new Date()): string {
  return businessDate(now);
}

/** The business-zone date before a given one — `2026-10-12` from `2026-10-13`. */
export function previousDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!match) throw new Error(`Not a date: ${date}`);

  const at = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) - 86_400_000,
  );
  return at.toISOString().slice(0, 10);
}
